import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { ComboboxSelect } from '@/components/ui/combobox-select';
import type { ComboboxOption } from '@/components/ui/combobox-select';
import { useStore } from '@/lib/store';
import { useUIConfig } from '@/config/ui-config';
import type {
  FeeSharingConfig,
  FeeSharingConfigPayload,
  FeeSharingType,
} from '@/types/fee-sharing';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Percent,
  Euro,
  CheckCircle2,
  Clock,
} from 'lucide-react';

// ─── Local form types ──────────────────────────────────────────────────────────

interface ParticipantEntry {
  id?: number;
  client: number | null;
  client_purchase_id: string;
  service_markup: string;
  fee_sharing_type: FeeSharingType;
  percentage_share: string;
  fixed_amount: string;
  installments: number;
  invoice_detail_level: string;
  // Read-only display fields
  fixed_amount_used?: string;
  fixed_amount_remaining?: string | null;
  amount_per_installment?: string | null;
  is_exhausted?: boolean;
}

interface ConfigEntry {
  /** Undefined = new unsaved config */
  id?: number;
  label: string;
  effective_from: string;
  is_active?: boolean;
  participants: ParticipantEntry[];
  /** Collapsed/expanded */
  expanded: boolean;
}

interface FeeSharingConfigsFormProps {
  initialConfigs?: FeeSharingConfig[];
  onChange?: (configs: FeeSharingConfigPayload[]) => void;
  firmId?: string;
  className?: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function apiConfigToEntry(cfg: FeeSharingConfig, expanded = false): ConfigEntry {
  return {
    id: cfg.id,
    label: cfg.label || '',
    effective_from: cfg.effective_from || '',
    is_active: cfg.is_active,
    participants: (cfg.participants || []).map((p) => ({
      id: p.id,
      client: p.client,
      client_purchase_id: p.client_purchase_id ?? '',
      service_markup: p.service_markup ?? '',
      fee_sharing_type: p.fee_sharing_type,
      percentage_share: p.percentage_share ?? '0.00',
      fixed_amount: p.fixed_amount ?? '',
      installments: p.installments ?? 1,
      invoice_detail_level: p.invoice_detail_level ?? '',
      fixed_amount_used: p.fixed_amount_used,
      fixed_amount_remaining: p.fixed_amount_remaining,
      amount_per_installment: p.amount_per_installment,
      is_exhausted: p.is_exhausted,
    })),
    expanded,
  };
}

function formatCurrency(value: string | number | null | undefined): string {
  if (value == null) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-BE', { style: 'currency', currency: 'EUR' }).format(num);
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function FeeSharingConfigsForm({ initialConfigs, onChange, firmId, className = '' }: FeeSharingConfigsFormProps) {
  const { clients } = useStore();
  const { form, theme } = useUIConfig(firmId);

  const [configs, setConfigs] = useState<ConfigEntry[]>([]);
  const isInitialized = useRef(false);

  // ── Initialize from parent data ────────────────────────────────────────────

  useEffect(() => {
    if (!isInitialized.current && initialConfigs) {
      const sorted = [...initialConfigs].sort(
        (a, b) => new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime()
      );
      setConfigs(sorted.map((c, i) => apiConfigToEntry(c, i === 0 || !!c.is_active)));
      isInitialized.current = true;
    }
  }, [initialConfigs]);

  // ── Notify parent of data changes ──────────────────────────────────────────

  const configsToPayloads = (entries: ConfigEntry[]): FeeSharingConfigPayload[] =>
    entries.map((cfg) => ({
      ...(cfg.id ? { id: cfg.id } : {}),
      label: cfg.label,
      effective_from: cfg.effective_from,
      participants: cfg.participants
        .filter((p) => p.client != null)
        .map((p) => ({
          ...(p.id ? { id: p.id } : {}),
          client: p.client!,
          client_purchase_id: p.client_purchase_id || null,
          service_markup: p.service_markup || null,
          fee_sharing_type: p.fee_sharing_type,
          invoice_detail_level: p.invoice_detail_level || '',
          ...(p.fee_sharing_type === 'percentage'
            ? { percentage_share: p.percentage_share }
            : { fixed_amount: p.fixed_amount, installments: p.installments }),
        })),
    }));

  useEffect(() => {
    if (isInitialized.current) {
      onChange?.(configsToPayloads(configs));
    }
  }, [configs]);

  // ── Config-level actions ───────────────────────────────────────────────────

  const addConfig = () => {
    const newConfig: ConfigEntry = {
      label: '',
      effective_from: new Date().toISOString().split('T')[0],
      participants: [
        { client: null, client_purchase_id: '', service_markup: '', fee_sharing_type: 'percentage', percentage_share: '100.00', fixed_amount: '', installments: 1, invoice_detail_level: '' },
      ],
      expanded: true,
    };
    setConfigs((prev) => [newConfig, ...prev]);
  };

  const updateConfig = (index: number, updates: Partial<ConfigEntry>) => {
    setConfigs((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const toggleExpanded = (index: number) => {
    setConfigs((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], expanded: !next[index].expanded };
      return next;
    });
  };

  const removeConfig = (index: number) => {
    setConfigs((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Participant-level actions ──────────────────────────────────────────────

  const addParticipant = (configIndex: number) => {
    setConfigs((prev) => {
      const next = [...prev];
      const cfg = { ...next[configIndex] };
      cfg.participants = [
        ...cfg.participants,
        { client: null, client_purchase_id: '', service_markup: '', fee_sharing_type: 'percentage', percentage_share: '0.00', fixed_amount: '', installments: 1, invoice_detail_level: '' },
      ];
      next[configIndex] = cfg;
      return next;
    });
  };

  const removeParticipant = (configIndex: number, partIndex: number) => {
    setConfigs((prev) => {
      const next = [...prev];
      const cfg = { ...next[configIndex] };
      if (cfg.participants.length <= 1) return prev;
      cfg.participants = cfg.participants.filter((_, i) => i !== partIndex);
      next[configIndex] = cfg;
      return next;
    });
  };

  const updateParticipant = (
    configIndex: number,
    partIndex: number,
    field: keyof ParticipantEntry,
    value: any
  ) => {
    setConfigs((prev) => {
      const next = [...prev];
      const cfg = { ...next[configIndex] };
      const participants = [...cfg.participants];
      participants[partIndex] = { ...participants[partIndex], [field]: value };

      // When switching type, reset the irrelevant field
      if (field === 'fee_sharing_type') {
        if (value === 'percentage') {
          participants[partIndex].fixed_amount = '';
          participants[partIndex].installments = 1;
        } else {
          participants[partIndex].percentage_share = '0.00';
        }
      }

      cfg.participants = participants;
      next[configIndex] = cfg;
      return next;
    });
  };

  const distributeEqually = (configIndex: number) => {
    setConfigs((prev) => {
      const next = [...prev];
      const cfg = { ...next[configIndex] };
      const pctParts = cfg.participants.filter((p) => p.fee_sharing_type === 'percentage');
      if (pctParts.length === 0) return prev;

      const share = Math.floor(10000 / pctParts.length) / 100;
      const remainder = 100 - share * pctParts.length;
      let distributed = 0;

      cfg.participants = cfg.participants.map((p) => {
        if (p.fee_sharing_type !== 'percentage') return p;
        let val = share;
        if (distributed < remainder) {
          val += 0.01;
          distributed += 0.01;
        }
        return { ...p, percentage_share: val.toFixed(2) };
      });
      next[configIndex] = cfg;
      return next;
    });
  };

  // ── Client combobox options (alphabetically sorted, searchable) ─────────

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => a.name.localeCompare(b.name)),
    [clients]
  );

  const allClientOptions: ComboboxOption[] = useMemo(
    () =>
      sortedClients.map((c) => ({
        value: String(c.id),
        label: c.name,
        searchableText: `${c.name} ${c.friendly_name || ''} ${c.vat_number || ''} ${c.email || ''}`,
      })),
    [sortedClients]
  );

  const getClientOptions = (configIndex: number, partIndex: number): ComboboxOption[] => {
    const cfg = configs[configIndex];
    const usedIds = new Set(
      cfg.participants
        .filter((_, i) => i !== partIndex)
        .map((p) => p.client)
        .filter(Boolean)
    );
    return allClientOptions.filter((o) => !usedIds.has(Number(o.value)));
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="h-5 w-1 rounded-full bg-primary/60" />
        <Label className={`text-base ${form.labelFontWeight}`}>Fee Sharing</Label>
      </div>

      {/* Config entries */}
      <div className="space-y-3">
        {configs.map((cfg, cfgIdx) => {
          const pctSum = cfg.participants
            .filter((p) => p.fee_sharing_type === 'percentage')
            .reduce((s, p) => s + parseFloat(p.percentage_share || '0'), 0);
          const pctInvalid = Math.abs(pctSum - 100) > 0.01;
          const isFuture = cfg.effective_from && new Date(cfg.effective_from) > new Date();

          return (
            <div
              key={cfg.id ?? `new-${cfgIdx}`}
              className={`border rounded-lg transition-colors ${
                cfg.is_active
                  ? 'border-primary/40 bg-primary/[0.02]'
                  : isFuture
                  ? 'border-dashed border-muted-foreground/30'
                  : theme.cardBorder ? 'border-border' : 'border-muted'
              } ${theme.tableHover ? 'hover:bg-muted/30' : ''}`}
            >
              {/* Config header — click to expand/collapse */}
              <div className={`flex items-center gap-3 px-4 py-2.5 ${cfg.is_active ? 'border-l-[3px] border-l-primary' : isFuture ? 'border-l-[3px] border-l-accent/40' : ''}`}>
                <button
                  type="button"
                  className="flex items-center gap-2 flex-1 min-w-0 text-left rounded-md px-2 py-1 -mx-2 -my-1 hover:bg-muted/50 transition-colors bg-transparent"
                  onClick={() => toggleExpanded(cfgIdx)}
                >
                  {cfg.expanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="font-medium text-sm truncate">
                    {cfg.label || (cfg.effective_from ? `Config from ${cfg.effective_from}` : 'New Configuration')}
                  </span>
                  {cfg.is_active && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px] shrink-0 dark:bg-emerald-900/30 dark:text-emerald-400">
                      <CheckCircle2 className="h-3 w-3 mr-0.5" />
                      Active
                    </Badge>
                  )}
                  {isFuture && !cfg.is_active && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      <Clock className="h-3 w-3 mr-0.5" />
                      Scheduled
                    </Badge>
                  )}
                </button>

                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => removeConfig(cfgIdx)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Expanded content */}
              {cfg.expanded && (
                <div className="border-t px-4 pb-3 pt-3 space-y-3">
                  {/* Config meta row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Label (optional)</Label>
                      <Input
                        type="text"
                        placeholder='e.g. "Phase 1 — with Company C"'
                        value={cfg.label}
                        className="h-8 text-sm"
                        onChange={(e) => updateConfig(cfgIdx, { label: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Start Date * <span className="text-muted-foreground/60">(DD/MM/YYYY)</span></Label>
                      <Input
                        type="text"
                        placeholder="DD/MM/YYYY"
                        value={cfg.effective_from
                          ? cfg.effective_from.split('-').reverse().join('/')
                          : ''}
                        className="h-8 text-sm"
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Allow typing freely; convert to ISO on valid input
                          const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                          if (match) {
                            updateConfig(cfgIdx, { effective_from: `${match[3]}-${match[2]}-${match[1]}` });
                          } else {
                            // Store raw temporarily so user can keep typing — store as-is to not lose input
                            updateConfig(cfgIdx, { effective_from: raw });
                          }
                        }}
                        onBlur={(e) => {
                          // On blur, try to parse and normalise
                          const raw = e.target.value;
                          const match = raw.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
                          if (match) {
                            const dd = match[1].padStart(2, '0');
                            const mm = match[2].padStart(2, '0');
                            updateConfig(cfgIdx, { effective_from: `${match[3]}-${mm}-${dd}` });
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Percentage total + actions bar */}
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium">Participants</Label>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${pctInvalid ? 'text-destructive bg-destructive/10' : 'text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20'}`}
                      >
                        {pctSum.toFixed(1)}% / 100%
                      </span>
                      {cfg.participants.filter((p) => p.fee_sharing_type === 'percentage').length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-6 text-[10px] px-2"
                          onClick={() => distributeEqually(cfgIdx)}
                        >
                          Equal Split
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* ── Compact participant table ─────────────────────────── */}
                  <div className="border rounded-md overflow-hidden">
                    {/* Table header */}
                    <div className="grid grid-cols-[minmax(0,1fr)_100px_110px_100px_80px_64px_90px_32px] gap-1 px-2 py-1.5 bg-primary/[0.06] text-[11px] font-semibold text-primary/70 dark:text-primary/80 uppercase tracking-wider">
                      <span>Client</span>
                      <span>Purchase ID</span>
                      <span>Type</span>
                      <span>Value</span>
                      <span>Markup %</span>
                      <span>Inst.</span>
                      <span>Inv. Format</span>
                      <span />
                    </div>

                    {/* Table rows */}
                    {cfg.participants.map((part, partIdx) => {
                      const isFixed = part.fee_sharing_type === 'fixed_amount';
                      const computedPerInstallment = isFixed && part.fixed_amount && part.installments >= 1
                        ? (parseFloat(part.fixed_amount) / part.installments)
                        : null;

                      return (
                        <div key={partIdx} className={`border-t ${partIdx % 2 === 1 ? 'bg-muted/30' : ''}`}>
                          {/* Main row */}
                          <div className="grid grid-cols-[minmax(0,1fr)_100px_110px_100px_80px_64px_90px_32px] gap-1 px-2 py-1.5 items-center">
                            {/* Client — searchable combobox */}
                            <ComboboxSelect
                              options={getClientOptions(cfgIdx, partIdx)}
                              value={part.client ? String(part.client) : undefined}
                              onValueChange={(val) =>
                                updateParticipant(cfgIdx, partIdx, 'client', Number(val))
                              }
                              placeholder="Search client…"
                              searchPlaceholder="Type to search…"
                              emptyMessage="No clients found."
                              triggerClassName="h-8 text-sm"
                              popoverClassName="w-[280px]"
                            />

                            {/* Purchase ID */}
                            <Input
                              type="text"
                              placeholder="PO-XXX"
                              value={part.client_purchase_id}
                              onChange={(e) =>
                                updateParticipant(cfgIdx, partIdx, 'client_purchase_id', e.target.value)
                              }
                              className="h-8 text-sm"
                            />

                            {/* Billing type */}
                            <Select
                              value={part.fee_sharing_type}
                              onValueChange={(val) =>
                                updateParticipant(cfgIdx, partIdx, 'fee_sharing_type', val)
                              }
                            >
                              <SelectTrigger className="h-8 text-xs bg-background">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">Percentage</SelectItem>
                                <SelectItem value="fixed_amount">Fixed €</SelectItem>
                              </SelectContent>
                            </Select>

                            {/* Value — share % or fixed amount */}
                            {part.fee_sharing_type === 'percentage' ? (
                              <div className="relative">
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder="100.00"
                                  value={part.percentage_share}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                      updateParticipant(cfgIdx, partIdx, 'percentage_share', v);
                                    }
                                  }}
                                  onBlur={() => {
                                    if (!part.percentage_share || part.percentage_share.trim() === '') {
                                      updateParticipant(cfgIdx, partIdx, 'percentage_share', '100.00');
                                    }
                                  }}
                                  className={`h-8 text-sm pr-7 ${pctInvalid ? 'border-destructive' : ''}`}
                                />
                                <Percent className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
                              </div>
                            ) : (
                              <div className="relative">
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder="0.00"
                                  value={part.fixed_amount}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                      updateParticipant(cfgIdx, partIdx, 'fixed_amount', v);
                                    }
                                  }}
                                  className="h-8 text-sm pr-7"
                                />
                                <Euro className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
                              </div>
                            )}

                            {/* Service Markup % */}
                            <Input
                              type="text"
                              inputMode="decimal"
                              placeholder="—"
                              value={part.service_markup}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^\d*\.?\d*$/.test(v)) {
                                  updateParticipant(cfgIdx, partIdx, 'service_markup', v);
                                }
                              }}
                              className="h-8 text-sm"
                              title="Service charge override %. Leave blank to use firm default."
                            />

                            {/* Installments */}
                            {isFixed ? (
                              <Input
                                type="text"
                                inputMode="numeric"
                                placeholder="1"
                                value={part.installments === 0 ? '' : String(part.installments)}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '') {
                                    updateParticipant(cfgIdx, partIdx, 'installments', 0);
                                  } else if (/^\d+$/.test(v)) {
                                    updateParticipant(cfgIdx, partIdx, 'installments', parseInt(v));
                                  }
                                }}
                                onBlur={() => {
                                  if (!part.installments || part.installments < 1) {
                                    updateParticipant(cfgIdx, partIdx, 'installments', 1);
                                  }
                                }}
                                className="h-8 text-sm"
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground text-center">—</span>
                            )}

                            {/* Invoice Detail Level */}
                            <Select
                              value={part.invoice_detail_level || '__default__'}
                              onValueChange={(val) =>
                                updateParticipant(cfgIdx, partIdx, 'invoice_detail_level', val === '__default__' ? '' : val)
                              }
                            >
                              <SelectTrigger className="h-8 text-[11px] bg-background">
                                <SelectValue placeholder="Default" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__default__">Default</SelectItem>
                                <SelectItem value="short">Short</SelectItem>
                                <SelectItem value="summary">Summary</SelectItem>
                                <SelectItem value="detailed">Detailed</SelectItem>
                              </SelectContent>
                            </Select>

                            {/* Remove */}
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => removeParticipant(cfgIdx, partIdx)}
                              disabled={cfg.participants.length <= 1}
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>

                          {/* Fixed-amount detail row: per-invoice + usage stats */}
                          {isFixed && (computedPerInstallment != null || (part.id && part.fixed_amount_used && parseFloat(part.fixed_amount_used) > 0)) && (
                            <div className="px-2 pb-1.5 pt-0">
                              <div className="flex items-center gap-3 ml-0 pl-2 border-l-2 border-primary/30 text-[11px]">
                                {computedPerInstallment != null && (
                                  <span className="text-muted-foreground">
                                    → <strong className="text-foreground">{formatCurrency(computedPerInstallment)}</strong> / invoice
                                  </span>
                                )}
                                {part.id && part.fixed_amount_used && parseFloat(part.fixed_amount_used) > 0 && (
                                  <>
                                    {computedPerInstallment != null && <span className="text-muted-foreground/40">|</span>}
                                    <span className="text-muted-foreground">
                                      Used: <strong className="text-foreground">{formatCurrency(part.fixed_amount_used)}</strong>
                                    </span>
                                    <span className="text-muted-foreground">
                                      Left: <strong className={part.is_exhausted ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}>
                                        {formatCurrency(part.fixed_amount_remaining)}
                                      </strong>
                                    </span>
                                    {part.is_exhausted && (
                                      <Badge variant="secondary" className="bg-red-100 text-red-700 text-[10px] dark:bg-red-900/30 dark:text-red-400">
                                        Fully Used
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Add client row */}
                    <div className="border-t">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full h-8 text-xs text-primary/70 hover:text-primary hover:bg-primary/[0.04] rounded-none border-0"
                        onClick={() => addParticipant(cfgIdx)}
                        disabled={cfg.participants.length >= clients.length}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Add Client
                      </Button>
                    </div>
                  </div>

                  {/* Validation warning */}
                  {pctInvalid && (
                    <div className="text-xs text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                      <strong>Warning:</strong> Percentage shares must total 100% (currently {pctSum.toFixed(2)}%).
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center">
        <Button type="button" variant="outline" size="sm" className="border-primary/30 text-primary hover:bg-primary/[0.06] hover:text-primary" onClick={addConfig}>
          <Plus className="h-4 w-4 mr-1.5" />
          Add Fee Sharing Configuration
        </Button>

        {configs.length === 0 && (
          <div className="text-xs text-muted-foreground bg-primary/[0.03] border border-dashed border-primary/15 p-3 rounded-md max-w-md">
            No fee sharing set up yet. Add a configuration to define how billing is split between clients.
          </div>
        )}
      </div>
    </div>
  );
}
